import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type CommandAction = {
  id: string;
  label: string;
  shortcut?: string;
  section: string;
  onAction: () => void;
};

type CommandPaletteProps = {
  open: boolean;
  onClose: () => void;
  actions: CommandAction[];
};

/** Simple fuzzy match: checks if all query chars appear in order. Returns score (lower = better) or -1 for no match. */
function fuzzyScore(text: string, query: string): number {
  const lower = text.toLowerCase();
  let qi = 0;
  let score = 0;
  let prevMatchIdx = -1;
  for (let i = 0; i < lower.length && qi < query.length; i++) {
    if (lower[i] === query[qi]) {
      // Bonus for consecutive matches
      score += (i === prevMatchIdx + 1) ? 0 : (i - prevMatchIdx);
      prevMatchIdx = i;
      qi++;
    }
  }
  return qi === query.length ? score : -1;
}

export function CommandPalette({ open, onClose, actions }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return actions;

    const scored = actions
      .map((a) => {
        const labelScore = fuzzyScore(a.label, q);
        const sectionScore = fuzzyScore(a.section, q);
        const best = labelScore >= 0 && sectionScore >= 0
          ? Math.min(labelScore, sectionScore)
          : labelScore >= 0 ? labelScore : sectionScore;
        return { action: a, score: best };
      })
      .filter((x) => x.score >= 0);

    scored.sort((a, b) => a.score - b.score);
    return scored.map((x) => x.action);
  }, [actions, query]);

  // Group by section
  const grouped = useMemo(() => {
    const map = new Map<string, CommandAction[]>();
    for (const action of filtered) {
      const existing = map.get(action.section);
      if (existing) existing.push(action);
      else map.set(action.section, [action]);
    }
    return map;
  }, [filtered]);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Scroll selected item into view
  useEffect(() => {
    const item = listRef.current?.querySelector('[data-selected="true"]');
    item?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const runSelected = useCallback(() => {
    const action = filtered[selectedIndex];
    if (action) {
      onClose();
      action.onAction();
    }
  }, [filtered, selectedIndex, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % Math.max(filtered.length, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) => (i - 1 + filtered.length) % Math.max(filtered.length, 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        runSelected();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [filtered.length, runSelected, onClose],
  );

  if (!open) return null;

  let flatIndex = 0;

  return (
    <div className="ds-modal-overlay" onClick={onClose} style={{ alignItems: 'flex-start', paddingTop: '20vh' }}>
      <div
        className="ds-cmd-palette"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <input
          ref={inputRef}
          className="ds-cmd-palette__input"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectedIndex(0);
          }}
          placeholder="Type a command..."
          spellCheck={false}
          autoComplete="off"
        />

        <div className="ds-cmd-palette__list" ref={listRef}>
          {filtered.length === 0 && (
            <div className="ds-cmd-palette__empty">No matching commands</div>
          )}

          {Array.from(grouped.entries()).map(([section, items]) => (
            <div key={section}>
              <div className="ds-cmd-palette__section">{section}</div>
              {items.map((action) => {
                const idx = flatIndex++;
                return (
                  <button
                    key={action.id}
                    type="button"
                    className={`ds-cmd-palette__item ${idx === selectedIndex ? 'ds-cmd-palette__item--selected' : ''}`}
                    data-selected={idx === selectedIndex}
                    onClick={() => {
                      onClose();
                      action.onAction();
                    }}
                    onMouseEnter={() => setSelectedIndex(idx)}
                  >
                    <span>{action.label}</span>
                    {action.shortcut && (
                      <kbd className="ds-cmd-palette__kbd">{action.shortcut}</kbd>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
